// Red / Amber / Green status for the Sprint Review numbers. Pure functions, no React.
// Every threshold lives in THRESHOLDS so the rules can be agreed and changed in one place.
// Each rule returns { tone: 'g' | 'a' | 'r' | 'n', reason } where 'n' means no data / not rated.

export const THRESHOLDS = {
  unlinkedPct: { green: 10, amber: 20 },           // share of effort not linked to the plan: <=10 green, <=20 amber, above red
  reliabilityPct: { green: 80, amber: 60 },        // forecast reliability: >=80 green, >=60 amber
  velocityVsAvg: { green: 0.8, amber: 0.6 },       // velocity as a share of the average: >=80% green, >=60% amber
  cycleP85Days: { green: 3, amber: 5 },            // 85th percentile cycle time: <=3 green, <=5 amber
  prodBugs: { green: 0, amber: 2 },                // production bugs raised: 0 green, <=2 amber
  automatedPct: { green: 75, amber: 50 },          // tests automated: above 75 green, 50 to 75 amber, below 50 red
  retroClosedPct: { green: 75, amber: 50 },        // retro actions closed: >=75 green, >=50 amber
  practice: { green: 3, amber: 2 },                // habits in place out of 4: >=3 green, 2 amber, fewer red
  pulse: { red: 2, green: 3.5 },                   // team pulse out of 5: <=2 red, below 3.5 amber, >=3.5 green
};

const rule = (tone, reason) => ({ tone, reason });
const none = (reason) => rule('n', reason);
const fmt = (n) => (Number.isInteger(n) ? String(n) : Number(n).toFixed(1));

const outcomeKnown = (o) => ['met', 'partly', 'not_met'].includes(o);

export const goalRag = (c) => {
  if (!c.goal.text) return rule('r', 'No sprint goal in ADO');
  if (!outcomeKnown(c.goal.outcome)) return rule('a', 'Goal set, outcome not recorded');
  return { met: rule('g', 'Goal met'), partly: rule('a', 'Goal partly met'), not_met: rule('r', 'Goal not met') }[c.goal.outcome];
};

export const commitRag = (delivery) => {
  if (!delivery.total) return none('No commitments for this team');
  if (delivery.moved === delivery.total) return rule('g', `All commitments moved (${delivery.moved} of ${delivery.total})`);
  if (delivery.moved > 0) return rule('a', `${delivery.moved} of ${delivery.total} commitments moved`);
  return rule('r', `No commitments moved (0 of ${delivery.total})`);
};

export const effortRag = (c) => {
  if (!c.allocation.total) return none('No completed items yet');
  const p = c.shares.unlinked;
  const t = THRESHOLDS.unlinkedPct;
  if (p <= t.green) return rule('g', `${p}% of effort not linked to the plan (green up to ${t.green}%)`);
  if (p <= t.amber) return rule('a', `${p}% of effort not linked to the plan (amber up to ${t.amber}%)`);
  return rule('r', `${p}% of effort not linked to the plan (red above ${t.amber}%)`);
};

export const reliabilityRag = (c) => {
  const v = c.forecast.reliability;
  if (v == null) return none('No planned items at sprint start');
  const t = THRESHOLDS.reliabilityPct;
  if (v >= t.green) return rule('g', `${v}% (green at ${t.green}% or more)`);
  if (v >= t.amber) return rule('a', `${v}% (amber ${t.amber}-${t.green - 1}%)`);
  return rule('r', `${v}% (red below ${t.amber}%)`);
};

export const average = (nums) => {
  const v = nums.filter((n) => n != null);
  return v.length ? v.reduce((s, n) => s + n, 0) / v.length : null;
};

/** hist is oldest first and includes the current sprint. */
export const velocityRag = (c, hist) => {
  const avg = average(hist.map((m) => m.velocity));
  if (!c.velocity || !avg) return none('Not enough history');
  const share = c.velocity / avg;
  const t = THRESHOLDS.velocityVsAvg;
  const txt = `${c.velocity} SP against an average of ${Math.round(avg)} SP`;
  if (share >= t.green) return rule('g', `${txt} (green at ${t.green * 100}% of average or more)`);
  if (share >= t.amber) return rule('a', `${txt} (amber at ${t.amber * 100}% or more)`);
  return rule('r', `${txt} (red below ${t.amber * 100}% of average)`);
};

export const cycleRag = (c) => {
  const v = c.cycle.p85;
  if (v == null) return none('No cycle time data');
  const t = THRESHOLDS.cycleP85Days;
  if (v <= t.green) return rule('g', `85th percentile ${v} days (green up to ${t.green})`);
  if (v <= t.amber) return rule('a', `85th percentile ${v} days (amber up to ${t.amber})`);
  return rule('r', `85th percentile ${v} days (red above ${t.amber})`);
};

export const bugsRag = (q) => {
  if (!q) return none('No quality data');
  const t = THRESHOLDS.prodBugs;
  if (q.bugsProduction <= t.green) return rule('g', `${q.bugsRaised} bugs raised, ${q.bugsProduction} in production`);
  if (q.bugsProduction <= t.amber) return rule('a', `${q.bugsProduction} production bugs raised (amber up to ${t.amber})`);
  return rule('r', `${q.bugsProduction} production bugs raised (red above ${t.amber})`);
};

export const automatedRag = (q) => {
  if (!q || !q.testsCreated) return none('No tests created this sprint');
  const p = Math.round((q.testsAutomated / q.testsCreated) * 100);
  const t = THRESHOLDS.automatedPct;
  if (p > t.green) return rule('g', `${p}% of tests automated (green above ${t.green}%)`);
  if (p >= t.amber) return rule('a', `${p}% of tests automated (amber ${t.amber}-${t.green}%)`);
  return rule('r', `${p}% of tests automated (red below ${t.amber}%)`);
};

/** The practice habits, in display order. null means not recorded. */
export const HABITS = [
  ['goalSet', 'Sprint goal set in ADO'],
  ['outcomeRecorded', 'Goal outcome recorded'],
  ['retroHeld', 'Sprint retro held'],
  ['checkinDone', 'Sprint retro recorded'],
];

export const practiceCount = (p) => HABITS.filter(([k]) => p?.[k] === true).length;

export const practiceRag = (c) => {
  const n = practiceCount(c.practice);
  const t = THRESHOLDS.practice;
  if (n >= t.green) return rule('g', `${n} of ${HABITS.length} habits in place`);
  if (n >= t.amber) return rule('a', `${n} of ${HABITS.length} habits in place`);
  return rule('r', `Only ${n} of ${HABITS.length} habits in place (red below ${t.amber})`);
};

export const retroRag = (c) => {
  if (!c.retro.committed) return none('No retro actions recorded');
  const p = Math.round((c.retro.closed / c.retro.committed) * 100);
  const t = THRESHOLDS.retroClosedPct;
  if (p >= t.green) return rule('g', `${c.retro.closed} of ${c.retro.committed} retro actions closed`);
  if (p >= t.amber) return rule('a', `${c.retro.closed} of ${c.retro.committed} retro actions closed`);
  return rule('r', `Only ${c.retro.closed} of ${c.retro.committed} retro actions closed`);
};

export const pulseTone = (v) => {
  if (v == null) return 'n';
  const t = THRESHOLDS.pulse;
  if (v <= t.red) return 'r';
  return v < t.green ? 'a' : 'g';
};

export const pulseRag = (v) => {
  const t = THRESHOLDS.pulse;
  if (v == null) return none('No pulse score yet');
  const tone = pulseTone(v);
  const why = { r: `red at ${t.red} or less`, a: `amber above ${t.red} and below ${t.green}`, g: `green at ${t.green} or more` }[tone];
  return rule(tone, `Pulse ${fmt(v)} (${why})`);
};

/** Pulse for the current sprint: survey average once enough people answered, else the check-in average. */
export const currentPulse = (data) => {
  const sum = data?.survey?.summary;
  if (sum && !sum.hidden && sum.overallMean != null) return sum.overallMean;
  return data?.current?.pulse?.avg ?? null;
};

/** Newest first, so the current sprint is leftmost. */
export const recent = (hist, n = 5) => [...hist].reverse().slice(0, n);

/** Items that need a person's attention, in the order they are shown. `action` names what the UI should do. */
export const attentionItems = (c) => {
  const out = [];
  if (!c.goal.text) {
    out.push({ key: 'goal', title: 'No sprint goal in ADO', detail: 'Leaders cannot judge the outcome without a goal.', action: 'sync', label: 'Sync from ADO' });
  } else if (!outcomeKnown(c.goal.outcome)) {
    out.push({ key: 'outcome', title: 'Goal outcome not recorded', detail: 'Mark the goal as Met, Partly or Not met.', action: 'goal', label: 'Record outcome' });
  }
  if (c.allocation.total && c.shares.unlinked > THRESHOLDS.unlinkedPct.amber) {
    const n = c.unlinkedItems?.length || 0;
    out.push({
      key: 'unlinked',
      title: `${c.shares.unlinked}% of effort is not linked to the plan`,
      detail: `${n} item${n === 1 ? '' : 's'}, ${c.allocation.unlinked} ${c.allocation.basis === 'sp' ? 'SP' : 'items'}.`,
      action: 'effort',
      label: 'Review items',
    });
  }
  if (!c.practice.checkinDone) {
    out.push({ key: 'retro', title: 'Sprint retro not recorded', detail: 'Mark the retro as held under Sprint retro before the leadership email is complete.', action: 'pulse', label: 'Go to sprint retro' });
  }
  return out;
};

export const RAG_LABEL = { g: 'Green', a: 'Amber', r: 'Red', n: 'No data' };
